import React from 'react';
import { Layout } from '@/components/Common';
import CustomHead from '@/components/Common/Layout/Head';
import { NextPageWithLayout } from '@/pages/_app';
import type { GetServerSidePropsContext, InferGetStaticPropsType } from 'next';
import { WEBSITE_URL } from '@/shared/constants';
import isEmpty from 'lodash/isEmpty';
import { businessSettingsDisplay, businessSettingsNone } from '@/lib/service';
import { BusinessSettingConfigKey } from '@/shared/types/business-setting';
import SourcingHomepage from '@/components/SourcingMarketplace/Home/SourcingHomepage';
import { getCatalogCategories, getCatalogItems, countCatalogItemsInSearch } from '@/lib/service/catalog';
import { ParamKey } from '@/shared/types/url-params';
import { CatalogCategory, CatalogItem, ProductSortKey } from '@/shared/types/catalog';
import { PRODUCTS_PAGE_SIZE } from '@/shared/constants/sourcing-marketplace';

export async function getServerSideProps(context: GetServerSidePropsContext) {
  try {
    const categoryConfigPromise = businessSettingsDisplay(BusinessSettingConfigKey.CREATOR_HUB_HOMEPAGE_CATEGORIES_V2);
    const logisticsFeeRateLadderPromise = businessSettingsNone(
      BusinessSettingConfigKey.DEFAULT_LOGISTICS_FEE_RATE_LADDER,
    );

    const searchKey = context.query?.[ParamKey.SEARCH] || '';
    let categoryList: CatalogCategory[] = [];
    let catalogItemList: CatalogItem[] = [];
    let searchCounts: { [key: string]: number } = {};

    if (searchKey) {
      const catalogCategoriesPromise = getCatalogCategories({
        page: 0,
        size: 9999,
      });

      const catalogItemsPromise = getCatalogItems({
        page: 0,
        size: PRODUCTS_PAGE_SIZE,
        sorts: ProductSortKey.FEATURED,
        keyword: searchKey as string,
      });

      const searchCountsPromise = countCatalogItemsInSearch({
        keyword: searchKey as string,
        groupField: 'category_id',
      });

      const [categoryRes, itemRes, searchCountsRes] = await Promise.all([
        catalogCategoriesPromise,
        catalogItemsPromise,
        searchCountsPromise,
      ]);

      categoryList = categoryRes.list;
      catalogItemList = itemRes.list;
      searchCounts = searchCountsRes;
    }

    const [categoryConfig, logisticsFeeRateLadder] = await Promise.all([
      categoryConfigPromise,
      logisticsFeeRateLadderPromise,
    ]);

    return {
      props: {
        categoryConfig: categoryConfig.value,
        logisticsFeeRateLadder: logisticsFeeRateLadder.value,
        categoryList,
        catalogItemList,
        searchCounts,
        searchKey,
      },
    };
  } catch (e) {
    return { notFound: true };
  }
}

const SourcingHome: NextPageWithLayout<InferGetStaticPropsType<typeof getServerSideProps>> = ({
  categoryConfig,
  logisticsFeeRateLadder,
  categoryList,
  catalogItemList,
  searchCounts,
  searchKey,
}) => {
  return (
    <>
      <SourcingHomepage categoryConfig={categoryConfig} logisticsFeeRateLadder={logisticsFeeRateLadder} />
    </>
  );
};

SourcingHome.getLayout = function getLayout(page: React.ReactElement) {
  return (
    <>
      <CustomHead
        title="Pietra | Custom product, packaging, and merch manufacturers"
        description="Pietra is the best platform to sample and customize products. Browse over 1,000 vetted suppliers to create apparel, makeup, jewelry, candles, and more."
        url={`${WEBSITE_URL}/sourcing`}
        image="https://static.pietrastudio.com/public_grande/file_uploads/567557a37f147ee5de05e2b54359c404.png"
      />
      <Layout hideFooter>{page}</Layout>
    </>
  );
};

export default SourcingHome;
